import { Component, ChangeDetectionStrategy, computed, effect, inject, input, output, signal, Input, EventEmitter, Output, OnInit, OnDestroy } from '@angular/core';
import { ScreeningListCreatorResponseItem, SocialMonitoringInquiryResponseDto } from '@/app/common/general-types/social-screening.dto';
import { ManageBriefTabsMonitorService } from '../../services/manage-brief-tabs-monitor/manage-brief-tabs-monitor';
import { LdgImgComponent } from '@/app/atoms/ldg-img/ldg-img.component';
import { ImageService } from '@/app/services/image/image.service';
import { TooltipDirective } from '@/app/common/directives/tooltip/tooltip.directive';
import { FormatService } from '@/app/common/format/format.service';
import { UploadCsv } from './atoms/upload-csv/upload-csv';
import { AddManually } from './atoms/add-manually/add-manually';
import { SocialMonitoring } from '@/app/services/social-monitoring/social-monitoring';
import { CurrentUserService } from '@/app/common/current-user/current-user.service';
import { SearchCreators } from './atoms/search-creators/search-creators';
import { firstValueFrom, Subscription } from 'rxjs';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { SuperSocialService } from '@/app/services/super-social/super-social.service';
import { ToastNotificationService } from '@/app/atoms/toast-notification/toast-notification.service';
import { Router } from '@angular/router';
import { RecruitGroupPicker } from '@/app/molecules/recruit-group-picker/recruit-group-picker';
import { isFindBriefContent } from '../mon-shell/monitor-shell';

@Component({
  selector: 'mbtm-mon-creators',
  imports: [RecruitGroupPicker, 
    LdgImgComponent,
    TooltipDirective,
    UploadCsv,
    AddManually,
    SearchCreators,
    InfiniteScrollDirective,
  ],
  templateUrl: './mon-creators.html',
  styleUrl: './mon-creators.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MonCreators implements OnInit, OnDestroy {

  @Input() focussedMonitoringBriefId!: string;
  @Output() creatorListSetOut = new EventEmitter<boolean>();

  readonly openPicker = input(false);
  readonly pickerOpened = output<void>();

  readonly pageSize = 30;

  addCreatorsMode = signal<boolean>(false);
  showAddCreatorPicker = signal<boolean>(false);
  creatorModeTab = signal<1 | 2 | 3 | 4>(2);

  pageNumber = signal<number>(0);
  inquiries = signal<SocialMonitoringInquiryResponseDto[]>([]);
  hasMore = signal<boolean>(true);
  isLoadingMore = signal<boolean>(false);

  hasInquiries = computed(() => (this.inquiries().length ?? 0) > 0);

  private subscriptionList = new Subscription();

  sendingToSuperSocial = signal<string[]>([]);
  pendingSuperSocial = signal<{ inquiryId: string; platform: 'tiktok' | 'instagram' }[]>([]);

  /** Services */
  private router = inject(Router);
  public manageBriefTabsMonitorService = inject(ManageBriefTabsMonitorService);
  public formatService = inject(FormatService);
  public imageService = inject(ImageService);
  public socialMonitoringService = inject(SocialMonitoring);
  private currentUserService = inject(CurrentUserService);
  private superSocialService = inject(SuperSocialService);
  private toastNotificationService = inject(ToastNotificationService);
  /** Services - END */

  /** Approved / Issues / Progress only mean something when posts are scored against the brief. */
  readonly showReviewColumns = computed(
    () => !isFindBriefContent(this.manageBriefTabsMonitorService.focussedMonitoringBrief()?.monitorType),
  );

  tableCreators = computed(() => {
    const list = this.manageBriefTabsMonitorService.creatorList();
    if (!list) {
      return [];
    }
    if (list.linkedCreators && list.linkedCreators.length) {
      return list.linkedCreators;
    }
    return list.creators ?? [];
  });


  constructor() {
    effect(() => {
      if (!this.openPicker()) {
        return;
      }
      this.openAddCreatorPicker();
      this.pickerOpened.emit();
    });
  }

  addCreatorOptions: { id: 1 | 2 | 3 | 4, name: string, description: string, class: string }[] = [
    { id: 1, name: `Search Creators`, description: `Search for creators by name, handle, or keyword.`, class: 'bg-teal-500/10 text-teal-400' },
    // { id: 2, name: `Add Manually`, description: `Enter creator details one by one.`, class: 'bg-rose-500/10 text-rose-400' },
    { id: 3, name: `Upload CSV`, description: `Upload a CSV using a template.`, class: 'bg-blue-500/10 text-blue-400' },
    // { id: 4, name: `Existing List`, description: `Use an existing list of creators.`, class: 'bg-pink-500/10 text-pink-400' },
  ]

  ngOnInit(): void {
    this.getInquiries();
  }

  ngOnDestroy(): void {
    this.subscriptionList.unsubscribe();
  }

  getInquiries(append = false): void {
    if (append) {
      if (this.isLoadingMore() || !this.hasMore()) {
        return;
      }
      this.isLoadingMore.set(true);
    } else {
      this.pageNumber.set(0);
      this.hasMore.set(true);
      this.isLoadingMore.set(false);
    }

    this.subscriptionList.add(
      this.socialMonitoringService
        .getInquiriesByBriefId(
          this.focussedMonitoringBriefId,
          this.pageSize,
          this.pageNumber() * this.pageSize,
        )
        .subscribe({
          next: (inquiries: SocialMonitoringInquiryResponseDto[]) => {
            console.log('getInquiries()', inquiries);
            const chunk = inquiries ?? [];

            if (append) {
              const existing = this.inquiries();
              this.inquiries.set([
                ...existing,
                ...chunk.filter((inquiry) => !existing.some((item) => item.id === inquiry.id)),
              ]);
            } else {
              this.inquiries.set(chunk);
            }

            this.pruneResolvedPendingSuperSocial();
            this.hasMore.set(chunk.length >= this.pageSize);
            this.isLoadingMore.set(false);
          },
          error: () => {
            this.isLoadingMore.set(false);
            if (append) {
              this.pageNumber.update((page) => Math.max(0, page - 1));
            } else {
              this.inquiries.set([]);
              this.hasMore.set(false);
            }
          },
        }),
    );
  }

  onScrollDown(): void {
    if (this.isLoadingMore() || !this.hasMore()) {
      return;
    }

    this.pageNumber.update((page) => page + 1);
    this.getInquiries(true);
  }


  creatorTrackId(index: number, creator: ScreeningListCreatorResponseItem): string {
    return creator.creatorId ?? creator.id ?? `${creator.name}-${index}`;
  }

  creatorPhotoSrc(creator: ScreeningListCreatorResponseItem): string | undefined {
    if (!creator.photoURL || !creator.photoURL.trim()) {
      return undefined;
    }
    return this.imageService.getImageSource(creator.photoURL, 'gallery');
  }

  creatorInitials(creator: ScreeningListCreatorResponseItem): string {
    const parts = creator.name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) {
      return '?';
    }
    if (parts.length === 1) {
      return parts[0].slice(0, 2).toUpperCase();
    }
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }

  instagramDisplay(creator: ScreeningListCreatorResponseItem): string {
    return this.formatSocialHandle(creator.instaHandle || creator.instagramHandle);
  }

  tikTokDisplay(creator: ScreeningListCreatorResponseItem): string {
    return this.formatSocialHandle(creator.tikTokHandle || creator.tiktokHandle);
  }

  private formatSocialHandle(handle: string | undefined): string {
    const h = handle && handle.trim();
    if (!h) {
      return '—';
    }
    return h.startsWith('@') ? h : `@${h}`;
  }

  onCreatorAction(creator: ScreeningListCreatorResponseItem): void {
    console.log('onCreatorAction()', creator);
  }

  onInquiryAction(inquiry: SocialMonitoringInquiryResponseDto): void {
    console.log('onInquiryAction()', inquiry);
    this.router.navigate(['/creator-inquiry', inquiry.id], {
      state: { monitoringBriefId: this.focussedMonitoringBriefId },
    });
  }

  isPendingSuperSocial(inquiryId: string, platform: 'tiktok' | 'instagram'): boolean {
    return this.pendingSuperSocial().some(
      (entry) => entry.inquiryId === inquiryId && entry.platform === platform,
    );
  }

  private markPendingSuperSocial(inquiryId: string, platform: 'tiktok' | 'instagram'): void {
    if (this.isPendingSuperSocial(inquiryId, platform)) {
      return;
    }
    this.pendingSuperSocial.update((entries) => [...entries, { inquiryId, platform }]);
  }

  private pruneResolvedPendingSuperSocial(): void {
    const inquiries = this.inquiries();
    this.pendingSuperSocial.update((entries) =>
      entries.filter((entry) => {
        const inquiry = inquiries.find((item) => item.id === entry.inquiryId);
        if (!inquiry?.creator?.ssCreator) {
          return true;
        }
        if (entry.platform === 'tiktok') {
          return !inquiry.creator.ssCreator.tiktokAccountId;
        }
        return !inquiry.creator.ssCreator.instagramAccountId;
      }),
    );
  }

  async sendToSuperSocial(inquiry: SocialMonitoringInquiryResponseDto, platform: 'tiktok' | 'instagram') {
    console.log('sendToSuperSocial()', inquiry);
    console.log('sendToSuperSocial() - which -> ', platform);
    this.sendingToSuperSocial.update((ids) => [...ids, inquiry.id]);

    /** Send to Super Social - TikTok */
    if (platform === 'tiktok' && inquiry.creator.tikTokHandle) {
      try {
        const tt_sent = await firstValueFrom(this.superSocialService.sendToSuperSocialTikTok(inquiry.creator.blazeFirebaseUid, inquiry.creator.tikTokHandle));
        console.log('tt_sent', tt_sent);
        if (tt_sent === false) {
          void this.toastNotificationService.alert({
            title: 'Error sending TikTok account to Super Social',
            message: 'No TikTok Handle',
            type: 'error',
            icon: 'error',
          });
        } else {
          this.markPendingSuperSocial(inquiry.id, 'tiktok');
          void this.toastNotificationService.alert({
            title: 'TikTok account sent to Super Social',
            message: 'TikTok account sent to Super Social',
            type: 'success',
            icon: 'success',
          });
        }
      } catch (error) {
        void this.toastNotificationService.alert({
          title: 'Error sending TikTok account to Super Social',
          message: 'Error sending TikTok account to Super Social',
          type: 'error',
          icon: 'error',
        });
        console.error('error', error);
      }
    }
    /** Send to Super Social - Instagram */
    if (platform === 'instagram' && inquiry.creator.instaHandle) {
      try {
        const ig_sent = await firstValueFrom(this.superSocialService.sendToSuperSocialInstagram(inquiry.creator.blazeFirebaseUid, inquiry.creator.instaHandle));
        console.log('ig_sent', ig_sent);
        if (ig_sent === false) {
          void this.toastNotificationService.alert({
            title: 'Error sending Instagram account to Super Social',
            message: 'No Instagram Handle',
            type: 'error',
            icon: 'error',
          });
        } else {
          this.markPendingSuperSocial(inquiry.id, 'instagram');
          void this.toastNotificationService.alert({
            title: 'Instagram account sent to Super Social',
            message: 'Instagram account sent to Super Social',
            type: 'success',
            icon: 'success',
          });
        }
      } catch (error) {
        void this.toastNotificationService.alert({
          title: 'Error sending Instagram account to Super Social',
          message: 'Error sending Instagram account to Super Social',
          type: 'error',
          icon: 'error',
        });
        console.error('error', error);
      }

    }
    /** Send to Super Social - END */

    this.sendingToSuperSocial.update((ids) => ids.filter((id) => id !== inquiry.id));
  }

  openAddCreatorPicker(): void {
    this.showAddCreatorPicker.set(true);
  }

  closeAddCreatorPicker(): void {
    this.showAddCreatorPicker.set(false);
  }

  selectCreatorOption(option: { id: 1 | 2 | 3 | 4, name: string, description: string, class: string }): void {
    console.log('selectCreatorOption()', option);
    this.showAddCreatorPicker.set(false);
    this.creatorModeTab.set(option.id);
    this.addCreatorsMode.set(true);
  }

  selectCreatorModeTab(tab: 1 | 2 | 3 | 4): void {

  }

  getTabClass(tab: 1 | 2 | 3 | 4) {
    return this.creatorModeTab() === tab
      ? 'bg-gray-200 text-gray-900 dark:bg-white/10 dark:text-gray-200'
      : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200';
  }

  creatorListSet(set: boolean): void {
    this.addCreatorsMode.set(false);
    this.creatorModeTab.set(2);
    this.creatorListSetOut.emit(set);
  }

  listUploaded(uploaded: boolean): void {
    this.inquiries.set([]);
    this.pageNumber.set(0);
    this.hasMore.set(true);
    this.isLoadingMore.set(false);
    this.addCreatorsMode.set(false);
    this.getInquiries();
    this.creatorListSetOut.emit(uploaded);
  }

}
